<template>
  <div class="pc-product-list">
    <div class="filter-bar">
      <div class="pc-container filter-panel">
        <div class="filter-row">
          <span class="filter-label">类目：</span>
          <div class="filter-options" :class="{ expanded: expanded.category }">
            <button v-if="selectedCategory" class="selected-option" @click="clearFilter('category')">
              {{ selectedCategory.name }}<b>×</b>
            </button>
            <template v-else>
              <button
                v-for="item in categoryOptions"
                :key="`${item.categoryId}-${item.subCategoryId || 0}`"
                class="option-button"
                @click="selectCategory(item)"
              >{{ item.name }}</button>
            </template>
          </div>
          <button v-if="!selectedCategory && categoryOptions.length > optionLimit" class="expand-button" @click="expanded.category = !expanded.category">
            {{ expanded.category ? '收起' : '展开' }}⌄
          </button>
          <button v-if="hasActiveFilters" class="clear-button" @click="clearAllFilters">清除 ×</button>
        </div>

        <div v-if="selectedChips.length" class="selected-filters">
          <span class="filter-label">已选：</span>
          <button v-for="chip in selectedChips" :key="chip.key" class="selected-chip" :title="'移除' + chip.label" @click="removeChip(chip)">{{ chip.label }} <b>×</b></button>
        </div>
      </div>
      <div class="pc-container parameter-panel">
        <div class="filter-heading">
          <h1>{{ selectedCategory ? selectedCategory.name : '全部商品' }}</h1>
          <div class="filter-layout" aria-label="筛选展示方式">
            <button :class="{ active: filterLayout === 'scroll' }" :aria-pressed="filterLayout === 'scroll'" @click="filterLayout = 'scroll'">滚动</button>
            <button :class="{ active: filterLayout === 'stack' }" :aria-pressed="filterLayout === 'stack'" @click="filterLayout = 'stack'">堆叠</button>
          </div>
        </div>
        <p v-if="filterLoading" class="filter-status" role="status">筛选项加载中...</p>
        <p v-else-if="filterError" class="filter-status" role="alert">{{ filterError }} <button @click="loadFilterOptions(true)">重新加载</button></p>
        <div ref="filterCards" class="parameter-cards" :class="{ stacked: filterLayout === 'stack' }" tabindex="0" aria-label="产品参数筛选">
          <product-filter-card
            v-for="card in filterCards"
            :key="card.key"
            :title="card.title"
            :options="card.options"
            :selected="card.selected"
            :value-unit="card.unit || ''"
            :numeric-ranges="card.numericRanges"
            :range="card.range"
            :disabled="Boolean(queryError)"
            @change="changeCard(card, $event)"
          />
        </div>
      </div>
    </div>

    <section class="pc-container list-wrap">
      <div class="sort-bar">
        <button :class="{ active: sortType === 'default' }" @click="setSort('default')">综合排序</button>
        <button :class="{ active: sortType === 'stock' }" @click="setSort('stock')">
          库存 <img class="sort-icon" :src="sortIcon('stock')" alt="">
        </button>
        <button :class="{ active: sortType === 'price' }" @click="setSort('price')">
          价格 <img class="sort-icon" :src="sortIcon('price')" alt="">
        </button>
        <button class="excel-search-button" @click="openExcelImport">Excel 导入搜索</button>
      </div>

      <div class="table-head">
        <span>型号/品牌</span>
        <span>参数/描述</span>
        <span>库存</span>
        <span>价格阶梯(含税)</span>
        <span>数量</span>
        <span>操作</span>
      </div>

      <div v-if="loading" class="empty">加载中...</div>
      <div v-else-if="queryError || productError" class="empty" role="alert">
        <p>{{ queryError || productError }}</p>
        <button v-if="queryError" class="retry-button" @click="clearAllFilters">清除筛选</button>
        <button v-else class="retry-button" @click="loadProducts">重新加载</button>
      </div>
      <div v-else-if="!products.length && searchKeyword" class="search-empty-ad">
        <div class="search-globe" aria-hidden="true">◎</div>
        <div class="search-empty-content">
          <h2>很抱歉，未找到 <strong>“{{ searchKeyword }}”</strong> 相关商品 <span>AI 搜索</span></h2>
          <p>您可直接联系专业客户经理帮您全球找货，<br>也可提交询价单，我们会第一时间与您核实洽谈！</p>
          <div class="search-empty-actions">
            <button class="manager-button" type="button" @click="showContactQr = true">专业客户经理全球找货</button>
            <router-link :to="sourcingRoute">提交询价单</router-link>
          </div>
          <div v-if="showContactQr && customerServiceQr" class="search-contact-qr">
            <img :src="customerServiceQr" alt="客户经理微信二维码">
            <span>扫码咨询</span>
          </div>
        </div>
      </div>
      <div v-else-if="!products.length" class="empty">
        <p>暂无符合条件的商品</p>
        <router-link class="sourcing-link" :to="sourcingRoute">在线找料</router-link>
      </div>

      <article v-for="item in products" :key="item.id" class="product-row">
        <div class="model-cell">
          <img class="product-img" :src="item.image" :alt="item.model || item.name" @error="handleProductImageError" @click="goDetail(item)">
          <div class="model-info">
            <h2 @click="goDetail(item)"><span class="self-tag">自营</span>{{ item.model || item.name }}</h2>
            <p>品牌：{{ item.brand }}</p>
            <p v-if="item.productType">类目：{{ item.productType }}</p>
            <p>封装：{{ item.package }}</p>
            <router-link class="datasheet-link" :to="productFilesRoute(item)" target="_blank" rel="noopener">
              <img :src="icons.file" alt="">数据手册
            </router-link>
          </div>
        </div>

        <div class="param-cell">
          <p v-for="param in item.parameterRows" :key="param.key" :title="`${param.label}：${param.value}`">
            <span>{{ param.label }}：</span>{{ param.value }}
          </p>
          <p v-if="!item.parameterRows.length" class="no-options">暂无参数描述</p>
        </div>

        <div class="stock-cell">
          <p>库存：<b>{{ item.stock }}</b></p>
          <p>起订量：{{ item.min }}</p>
          <p>包装数量：{{ item.packagingQuantity == null ? '--' : item.packagingQuantity }}</p>
          <p>重量：{{ item.weightGrams == null ? '--' : item.weightGrams + 'g' }}</p>
          <p>增量：{{ item.increment }}</p>
        </div>

        <div class="price-cell">
          <template v-if="item.hasPrice">
            <p v-for="(row, index) in displayTiers(item)" :key="row.minQuantity" :class="{ first: index === 0 }">
              {{ row.minQuantity }}+ <b>¥ {{ row.price }}</b>
            </p>
          </template>
          <p v-else class="no-price">暂无价格/联系询价</p>
        </div>

        <div class="quantity-cell">
          <div class="stepper">
            <button @click="changeQuantity(item, -item.increment)"><img :src="icons.minus" alt=""></button>
            <quantity-input :value="item.quantity" :min="item.min || 1" :max="item.stock" :disabled="!canPurchase(item)" @input="handleQuantityInput(item, $event)" />
            <button @click="changeQuantity(item, item.increment)"><img :src="icons.plus" alt=""></button>
          </div>
          <p v-if="item.hasPrice">合计 <b>¥ {{ itemTotal(item) }}</b></p>
          <p v-else class="no-price">联系询价</p>
          <span>现在下单 <strong>最快2小时发货</strong></span>
        </div>

        <div class="action-cell">
          <button class="cart-btn" :disabled="item.hasPrice && !canPurchase(item)" @click="addToCart(item)">{{ purchaseButtonText(item) }}</button>
          <button v-if="item.hasPrice" class="buy-btn" :disabled="!canPurchase(item)" @click="buyNow(item)">直接购买</button>
        </div>
      </article>
      <div v-if="!queryError && !productError && total > 0" class="pagination-bar">
        <span>共 {{ total }} 件商品</span>
        <a-pagination :current="page" :page-size="pageSize" :total="total" :disabled="loading" :show-size-changer="true" :page-size-options="['20', '50', '100']" @change="changePage" @showSizeChange="changePageSize" />
      </div>
    </section>

    <a-modal v-model="excelVisible" title="Excel 导入搜索产品" :width="860" :footer="null">
      <div class="excel-import-panel">
        <input ref="excelInput" type="file" accept=".xlsx,.xls" @change="handleExcelFile">
        <p>导入格式与 BOM 模板一致，系统会按型号、品牌和封装搜索相关产品。</p>
        <div class="excel-actions"><a-button type="primary" :loading="excelLoading" @click="$refs.excelInput.click()">选择 Excel 文件</a-button><a href="/file/bom-template.xlsx" download>下载导入模板</a></div>
      </div>
      <div v-if="excelResult" class="excel-result">
        <div class="excel-summary">共解析 {{ excelRows.length }} 行，匹配 {{ matchedExcelRows.length }} 个产品</div>
        <div v-for="row in excelRows" :key="row.rowNumber" class="excel-row">
          <div><b>{{ requirementText(row) }}</b><span>{{ row.matchMessage || matchStatusText(row.matchStatus) }}</span></div>
          <button v-if="row.product" @click="goExcelProduct(row.product)">查看 {{ row.product.model || row.product.name }}</button>
          <span v-else class="unmatched">暂无匹配产品</span>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
  import { message } from 'ant-design-vue'
  import { getPcCategoryTree, getPcMallSettings, getPcProductFilterOptions, getPcProductList, uploadPcProductSearch } from '@/api/pc'
  import commerce from '@/utils/pcCommerce'
  import QuantityInput from '@/components/pc/QuantityInput.vue'
  import { addCartItem, prepareBuyNowItem } from '@/utils/pcCart'
  import { buildPcDetailPath } from '@/utils/pcRouteCode'
  import ProductFilterCard from './product-filter-card.vue'
  import { readListQuery, filterQuery, buildListRequest, normalizeFilterOptions, displayParamName, displayParamValue, productRows } from './product-list-filters'

  const pcIcons = {
    up: require('@/assets/icons/pc/chevron-up.svg'),
    down: require('@/assets/icons/pc/chevron-down.svg'),
    plus: require('@/assets/icons/pc/plus.svg'),
    minus: require('@/assets/icons/pc/minus.svg'),
    file: require('@/assets/icons/pc/invoice.svg')
  }

  export default {
    name: 'PcProductList',
    components: { ProductFilterCard, QuantityInput },
    data() {
      return {
        categoryTree: [],
        categoryLoading: false,
        filterOptions: normalizeFilterOptions(),
        filterOptionsLoaded: false,
        filterLoading: false,
        filterError: '',
        filterScope: '',
        filterRequestId: 0,
        productRequestId: 0,
        filterLayout: 'scroll',
        queryError: '',
        productError: '',
        selectedGroupId: '',
        selectedCategoryId: '',
        selectedSubCategoryId: '',
        selectedFilters: { brands: [], packages: [], parameters: [] },
        optionLimit: 14,
        expanded: { category: false },
        products: [],
        page: 1,
        pageSize: 20,
        total: 0,
        loading: false,
        sortType: 'default',
        sortOrder: 'asc',
        icons: pcIcons,
        excelVisible: false,
        excelLoading: false,
        excelResult: null,
        mallSetting: {},
        showContactQr: true
      }
    },
    computed: {
      categoryOptions() {
        return this.categoryTree.reduce((options, category) => {
          const children = Array.isArray(category.children) ? category.children : []
          if (!children.length) {
            if (!this.filterOptionsLoaded || this.filterOptions.categoryIds.some(id => String(id) === String(category.id))) {
              options.push({ name: category.name, categoryId: category.id, subCategoryId: '' })
            }
            return options
          }
          children.forEach(item => {
            if (!this.filterOptionsLoaded || this.filterOptions.subCategoryIds.some(id => String(id) === String(item.id))) {
              options.push({ name: item.name, categoryId: category.id, subCategoryId: item.id })
            }
          })
          return options
        }, [])
      },
      selectedCategory() {
        if (this.selectedGroupId) {
          return { name: String(this.$route.query.title || '组合类目') }
        }
        const root = this.categoryTree.find(item => String(item.id) === String(this.selectedCategoryId))
        if (this.selectedSubCategoryId) {
          for (const category of this.categoryTree) {
            const child = (category.children || []).find(item => String(item.id) === String(this.selectedSubCategoryId))
            if (child) return { name: child.name, categoryId: category.id, subCategoryId: child.id }
          }
        }
        return root ? { name: root.name, categoryId: root.id, subCategoryId: '' } : null
      },
      hasActiveFilters() {
        return Boolean(this.selectedGroupId || this.selectedCategoryId || this.selectedSubCategoryId || this.selectedChips.length || this.queryError)
      },
      filterCards() {
        const cards = [
          { key: 'brands', title: '品牌', options: this.filterOptions.brands, selected: this.selectedFilters.brands, numericRanges: [], range: {} },
          { key: 'packages', title: '封装/规格', options: this.filterOptions.packages, selected: this.selectedFilters.packages, numericRanges: [], range: {} }
        ]
        const parameters = this.filterOptions.parameters.slice()
        // 当前条件即使暂时没有候选，也保留卡片，便于直接取消。
        this.selectedFilters.parameters.forEach(selected => {
          if (!parameters.some(item => item.name === selected.name)) parameters.push({ name: selected.name, values: [], numericRanges: [] })
        })
        parameters.forEach(item => {
          const selected = this.selectedFilters.parameters.find(param => param.name === item.name) || {}
          cards.push({ key: `param:${item.name}`, title: displayParamName(item.name), name: item.name, options: item.values,
            unit: item.unit, selected: selected.values || [], numericRanges: item.numericRanges, range: selected.values ? {} : selected })
        })
        return cards
      },
      selectedChips() {
        const chips = []
        const groups = [['brands', '品牌'], ['packages', '封装']]
        groups.forEach(([type, label]) => {
          this.selectedFilters[type].forEach(value => chips.push({ key: `${type}:${value}`, type, value, label: `${label}：${value}` }))
        })
        this.selectedFilters.parameters.forEach(param => {
          if (param.values) {
            const definition = this.filterOptions.parameters.find(item => item.name === param.name)
            param.values.forEach(value => chips.push({ key: `param:${param.name}:${value}`, type: 'parameter', name: param.name, value, label: `${displayParamName(param.name)}：${displayParamValue(value, definition && definition.unit)}` }))
          } else {
            const rangeText = param.min !== '' && param.max !== '' ? `${param.min} ～ ${param.max}` : (param.min !== '' ? `≥ ${param.min}` : `≤ ${param.max}`)
            chips.push({ key: `range:${param.name}`, type: 'range', name: param.name, label: `${displayParamName(param.name)}：${rangeText} ${param.unit}` })
          }
        })
        return chips
      },
      sourcingRoute() {
        return { path: '/pc/sourcing', query: { model: String(this.$route.query.keyword || '') } }
      },
      searchKeyword() { return String(this.$route.query.keyword || '').trim() },
      customerServiceQr() { return this.mallSetting.wechatQrCode ? commerce.normalizeAssetPath(this.mallSetting.wechatQrCode) : '' },
      excelRows() { return (this.excelResult && this.excelResult.rows) || [] },
      matchedExcelRows() { return this.excelRows.filter(row => row.product) }
    },
    watch: {
      '$route.query': {
        handler() {
          this.syncFiltersFromRoute()
          this.loadCategoryTree()
          this.loadFilterOptions()
          this.loadProducts()
        },
        immediate: true
      }
    },
    created() {
      getPcMallSettings().then(data => { this.mallSetting = data || {} }).catch(() => { this.mallSetting = {} })
    },
    beforeDestroy() {
      // 使离开页面前发出的请求失效，避免响应回写已销毁的页面。
      this.productRequestId += 1
      this.filterRequestId += 1
    },
    methods: {
      openExcelImport() { this.excelVisible = true },
      handleExcelFile(event) {
        const file = event.target.files && event.target.files[0]
        event.target.value = ''
        if (!file) return
        if (!/\.(xlsx|xls)$/i.test(file.name || '')) return message.warning('仅支持 .xlsx 或 .xls 文件')
        if (file.size > 10 * 1024 * 1024) return message.warning('Excel 文件不能超过 10MB')
        this.excelLoading = true
        uploadPcProductSearch(file).then(data => { this.excelResult = data || {}; message.success('产品搜索完成') })
          .catch(error => message.error(error.message || 'Excel 导入失败')).finally(() => { this.excelLoading = false })
      },
      requirementText(row) {
        const item = row.requirement || {}
        return [item.model, item.brand, item.packageName].filter(Boolean).join(' / ') || `第 ${row.rowNumber} 行`
      },
      matchStatusText(status) { return { EXACT: '完全匹配', PENDING: '找到相关产品', UNMATCHED: '无法匹配' }[status] || '匹配完成' },
      goExcelProduct(product) { this.excelVisible = false; this.$router.push(buildPcDetailPath('product', product.id)) },
      syncFiltersFromRoute() {
        const query = this.$route.query || {}
        this.selectedGroupId = query.groupId || ''
        this.selectedCategoryId = query.categoryId || ''
        this.selectedSubCategoryId = query.subCategoryId || ''
        this.queryError = ''
        try {
          const state = readListQuery(query)
          this.selectedFilters = { brands: state.brands, packages: state.packages, parameters: state.parameters }
          this.page = state.page
          this.pageSize = state.pageSize
          this.sortType = state.sortField
          this.sortOrder = state.sortOrder
        } catch (error) {
          this.queryError = error.message
          this.selectedFilters = { brands: [], packages: [], parameters: [] }
        }
      },
      loadCategoryTree() {
        if (this.categoryTree.length || this.categoryLoading) return
        this.categoryLoading = true
        getPcCategoryTree().then(data => {
          this.categoryTree = Array.isArray(data) ? data : []
        }).catch(() => {
          this.categoryTree = []
        }).finally(() => { this.categoryLoading = false })
      },
      loadFilterOptions(force = false) {
        const query = this.$route.query || {}
        const params = { keyword: query.keyword || '', groupId: query.groupId || '', categoryId: query.categoryId || '', subCategoryId: query.subCategoryId || '' }
        const scope = JSON.stringify(params)
        if (!force && this.filterScope === scope && (this.filterLoading || this.filterOptionsLoaded)) return
        this.filterScope = scope
        const requestId = ++this.filterRequestId
        this.filterOptionsLoaded = false
        this.filterLoading = true
        this.filterError = ''
        this.filterOptions = normalizeFilterOptions()
        return getPcProductFilterOptions(params).then(data => {
          if (requestId !== this.filterRequestId) return
          this.filterOptions = normalizeFilterOptions(data)
          this.filterOptionsLoaded = true
        }).catch(() => {
          if (requestId !== this.filterRequestId) return
          this.filterError = '筛选项加载失败，请重试'
        }).finally(() => {
          if (requestId === this.filterRequestId) this.filterLoading = false
        })
      },
      loadProducts() {
        const requestId = ++this.productRequestId
        this.products = []
        this.total = 0
        this.productError = ''
        if (this.queryError) {
          this.loading = false
          return
        }
        this.loading = true
        const state = { ...this.selectedFilters, page: this.page, pageSize: this.pageSize, sortField: this.sortType, sortOrder: this.sortOrder }
        return getPcProductList(buildListRequest(this.$route.query || {}, state)).then(pageData => {
          if (requestId !== this.productRequestId) return
          if (!pageData || !Array.isArray(pageData.records) || pageData.total == null || !Number.isSafeInteger(Number(pageData.total)) || Number(pageData.total) < 0) {
            throw new Error('商品列表返回异常，请重试')
          }
          this.total = Number(pageData.total)
          const lastPage = Math.max(1, Math.ceil(this.total / this.pageSize))
          if (this.page > lastPage) {
            this.updateRouteFilters({ page: lastPage }, true)
            return
          }
          this.products = pageData.records.map(this.normalizeRow)
        }).catch(error => {
          if (requestId !== this.productRequestId) return
          this.products = []
          this.total = 0
          this.productError = error.message || '商品加载失败，请重试'
        }).finally(() => {
          if (requestId === this.productRequestId) this.loading = false
        })
      },
      normalizeRow(item) {
        const product = commerce.normalizeProduct(item)
        return {
          ...product,
          package: item.packageName || '-',
          categoryName: item.categoryName,
          subCategoryName: item.subCategoryName,
          model: item.model || product.name,
          productType: (item.name && item.name !== item.model ? item.name : '') || item.subCategoryName || item.categoryName || '',
          desc: item.summary || item.desc || '',
          parameterRows: productRows(item),
          quantity: product.min
        }
      },
      updateRouteFilters(patch, replace = false) {
        const query = { ...(this.$route.query || {}), page: 1, ...patch }
        Object.keys(query).forEach(key => {
          if (query[key] === '' || query[key] === null || typeof query[key] === 'undefined') delete query[key]
        })
        this.$router[replace ? 'replace' : 'push']({ path: '/pc/products', query }).catch(() => {})
      },
      selectCategory(item) {
        this.expanded.category = false
        this.updateRouteFilters({
          groupId: '',
          categoryId: item.categoryId,
          subCategoryId: item.subCategoryId || '',
          brand: '',
          packageName: '',
          filters: ''
        })
      },
      clearFilter(type) {
        if (type === 'category') this.clearAllFilters()
      },
      clearAllFilters() {
        this.updateRouteFilters({ groupId: '', categoryId: '', subCategoryId: '', brand: '', packageName: '', filters: '', title: '' })
      },
      changeCard(card, selection) {
        const filters = { ...this.selectedFilters }
        if (card.key === 'brands' || card.key === 'packages') filters[card.key] = selection.values
        else {
          filters.parameters = filters.parameters.filter(item => item.name !== card.name)
          if (!selection.values || selection.values.length) filters.parameters.push({ name: card.name, ...selection })
        }
        // 立即同步选择，连续勾选时不依赖上一轮路由更新完成。
        this.selectedFilters = filters
        this.updateRouteFilters(filterQuery(filters))
      },
      removeChip(chip) {
        if (chip.type === 'brands' || chip.type === 'packages') {
          this.changeCard({ key: chip.type }, { values: this.selectedFilters[chip.type].filter(value => value !== chip.value) })
        } else {
          const param = this.selectedFilters.parameters.find(item => item.name === chip.name)
          this.changeCard({ key: `param:${chip.name}`, name: chip.name }, { values: chip.type === 'range' ? [] : param.values.filter(value => value !== chip.value) })
        }
      },
      changePage(page, pageSize) {
        this.updateRouteFilters({ page, pageSize })
      },
      changePageSize(current, pageSize) {
        this.updateRouteFilters({ page: 1, pageSize })
      },
      displayTiers(item) {
        if (!item.hasPrice) return []
        return item.priceTiers.length ? item.priceTiers.slice(0, 3) : [{ minQuantity: item.min, price: item.price }]
      },
      setSort(type) {
        const sortOrder = this.sortType === type && type !== 'default' && this.sortOrder === 'asc' ? 'desc' : 'asc'
        this.updateRouteFilters({ sortField: type, sortOrder })
      },
      sortIcon(type) {
        if (this.sortType !== type) return pcIcons.down
        return this.sortOrder === 'asc' ? pcIcons.up : pcIcons.down
      },
      changeQuantity(item, delta) {
        item.quantity = commerce.normalizeQuantity(commerce.toNumber(item.quantity, item.min || 1) + delta, item.min, item.stock)
      },
      handleQuantityInput(item, value) {
        item.quantity = commerce.normalizeQuantity(value, item.min, item.stock)
      },
      canPurchase(item) {
        return item.hasPrice && Number(item.stock || 0) >= Number(item.min || 1)
      },
      purchaseButtonText(item) {
        if (!item.hasPrice) return '联系询价'
        return this.canPurchase(item) ? '加入购物车' : '库存不足'
      },
      itemTotal(item) {
        return commerce.formatMoney(commerce.getTierPrice(item, item.quantity) * item.quantity, 2)
      },
      addToCart(item) {
        if (!item.hasPrice) {
          this.$router.push({ path: '/pc/sourcing', query: { model: item.model || item.name || '' } })
          return
        }
        if (!this.canPurchase(item)) return message.warning('该商品库存不足，暂时无法购买')
        addCartItem(item, item.quantity)
        this.$emit('cart-change')
        message.success('已加入购物车')
      },
      buyNow(item) {
        if (!this.canPurchase(item)) return message.warning('该商品库存不足，暂时无法购买')
        prepareBuyNowItem(item, item.quantity)
        this.$emit('cart-change')
        this.$router.push('/pc/checkout')
      },
      handleProductImageError(event) {
        commerce.handleProductImageError(event)
      },
      goDetail(item) {
        this.$router.push(buildPcDetailPath('product', item.id))
      },
      productFilesRoute(item) {
        return {
          path: buildPcDetailPath('product', item && item.id),
          query: { tab: 'files' },
          hash: '#product-files'
        }
      }
    }
  }
</script>

<style scoped>
  .pc-product-list {
    padding-bottom: 42px;
    background: #f3f5f9;
  }

  .filter-bar {
    padding: 10px 0 4px;
    background: #f3f5f9;
  }

  .filter-panel {
    padding: 0 16px;
    border-radius: 10px;
    background: #fff;
  }

  .filter-row {
    position: relative;
    min-height: 48px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid #edf0f4;
    font-size: 13px;
  }

  .filter-row:last-child {
    border-bottom: 0;
  }

  .filter-label {
    flex: 0 0 54px;
    color: #475569;
  }

  .filter-options {
    max-height: 48px;
    padding: 7px 100px 7px 0;
    flex: 1;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px 18px;
    overflow: hidden;
  }

  .filter-options.expanded {
    max-height: none;
  }

  .option-button,
  .selected-option,
  .expand-button,
  .clear-button {
    height: 28px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #1f2937;
    white-space: nowrap;
    cursor: pointer;
  }

  .option-button:hover {
    color: #1684ff;
  }

  .selected-option {
    position: relative;
    padding: 0 14px;
    border: 1px solid #1684ff;
    border-radius: 4px;
    background: #fff;
    color: #1684ff;
  }

  .selected-option b {
    position: absolute;
    top: -7px;
    right: -4px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #1684ff;
    color: #fff;
    font-size: 10px;
    line-height: 11px;
  }

  .expand-button,
  .clear-button {
    position: absolute;
    right: 4px;
    color: #475569;
  }

  .clear-button {
    top: 10px;
  }

  .filter-row:first-child .expand-button {
    right: 74px;
  }

  .no-options {
    color: #9ca3af;
  }

  .selected-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 10px 0; }
  .selected-chip { max-width: 100%; padding: 4px 8px; border: 1px solid #b7d9ff; border-radius: 4px; background: #f3f8ff; color: #1684ff; font-size: 12px; overflow-wrap: anywhere; cursor: pointer; }
  .selected-chip b { margin-left: 5px; }
  .parameter-panel { padding-top: 18px; }
  .filter-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .filter-heading h1 { margin: 0; color: #111827; font-size: 18px; font-weight: 700; }
  .filter-layout { display: flex; border: 1px solid #1684ff; border-radius: 5px; overflow: hidden; }
  .filter-layout button { height: 31px; padding: 0 17px; border: 0; background: #fff; color: #1684ff; cursor: pointer; }
  .filter-layout button + button { border-left: 1px solid #1684ff; }
  .filter-layout button.active { background: #1684ff; color: #fff; }
  .parameter-cards { display: flex; gap: 12px; padding: 2px 0 14px; overflow-x: auto; overscroll-behavior-x: contain; scrollbar-width: auto; scrollbar-color: #a8b2bf #e7ebf1; }
  .parameter-cards::-webkit-scrollbar { height: 9px; }
  .parameter-cards::-webkit-scrollbar-track { background: #e7ebf1; border-radius: 5px; }
  .parameter-cards::-webkit-scrollbar-thumb { background: #a8b2bf; border-radius: 5px; }
  .parameter-cards.stacked { flex-wrap: wrap; overflow-x: visible; }
  .parameter-cards.stacked > * { flex: 0 0 calc((100% - 36px) / 4); }
  .filter-status { margin: 0 0 12px; color: #64748b; font-size: 13px; }
  .filter-status button { padding: 0 8px; border: 0; background: transparent; color: #1684ff; cursor: pointer; }
  .pagination-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px; background: #fff; border-radius: 8px; color: #64748b; }
  .sourcing-link, .retry-button { display: inline-block; padding: 8px 22px; border: 1px solid #1684ff; border-radius: 5px; background: #fff; color: #1684ff; cursor: pointer; }
  .param-cell p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .list-wrap {
    min-height: 650px;
    padding-top: 12px;
  }

  .sort-bar {
    height: 46px;
    padding: 6px 12px;
    border-radius: 8px 8px 0 0;
    background: #fff;
  }

  .sort-bar .excel-search-button { float: right; margin-right: 0; border: 1px solid #1684ff; border-radius: 5px; }
  .excel-import-panel{padding:20px;border:1px dashed #b8c4d6;border-radius:8px;background:#f8fbff;text-align:center}.excel-import-panel input{display:none}.excel-import-panel p{color:#64748b}.excel-actions{display:flex;align-items:center;justify-content:center;gap:18px}.excel-result{max-height:440px;margin-top:18px;overflow:auto}.excel-summary{margin-bottom:10px;color:#475569}.excel-row{padding:12px 4px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #edf0f4}.excel-row>div{display:flex;flex-direction:column;gap:5px}.excel-row span{color:#8a8f99}.excel-row button{padding:7px 12px;border:1px solid #1684ff;border-radius:4px;background:#fff;color:#1684ff}.excel-row .unmatched{color:#e34848}

  .sort-bar button {
    height: 34px;
    margin-right: 8px;
    padding: 0 14px;
    border: 0;
    background: #fff;
    color: #1684ff;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .sort-bar .active {
    background: #1684ff;
    color: #fff;
  }

  .sort-icon {
    width: 12px;
    height: 12px;
  }

  .sort-bar .active .sort-icon {
    filter: brightness(0) invert(1);
  }

  .table-head,
  .product-row {
    display: grid;
    grid-template-columns: minmax(280px, 1.55fr) minmax(220px, 1.2fr) repeat(4, minmax(0, 1fr));
    column-gap: 14px;
  }

  .table-head {
    height: 42px;
    padding: 0 16px;
    align-items: center;
    border-bottom: 1px solid #edf0f4;
    background: #fff;
    color: #111827;
    font-weight: 700;
  }

  .product-row {
    min-height: 166px;
    margin-bottom: 14px;
    padding: 16px;
    border-radius: 0 0 10px 10px;
    background: #fff;
    box-shadow: 0 8px 20px rgba(15, 23, 42, .04);
  }

  .model-cell {
    display: grid;
    grid-template-columns: 96px minmax(0, 1fr);
    gap: 10px 12px;
    min-width: 0;
  }

  .param-cell,
  .stock-cell,
  .price-cell,
  .quantity-cell,
  .action-cell {
    min-width: 0;
  }

  .product-img {
    width: 96px;
    height: 96px;
    border-radius: 4px;
    object-fit: contain;
    background: #f6f7fb;
    cursor: pointer;
  }

  .model-info {
    min-width: 0;
  }

  .model-info h2 {
    margin: 0 0 8px;
    color: #111827;
    font-size: 15px;
    line-height: 20px;
    cursor: pointer;
    overflow-wrap: anywhere;
  }

  .self-tag {
    display: inline-block;
    margin-right: 4px;
    padding: 1px 4px;
    border-radius: 2px;
    background: #1684ff;
    color: #fff;
    font-size: 12px;
    line-height: 16px;
    vertical-align: 1px;
  }

  .model-info p,
  .param-cell p,
  .stock-cell p,
  .price-cell p,
  .quantity-cell p {
    margin: 0 0 7px;
    color: #4b5563;
    line-height: 18px;
  }

  .datasheet-link {
    width: fit-content;
    height: 18px;
    margin: 0 0 7px;
    padding: 0 8px;
    border: 1px solid #e5e7eb;
    border-radius: 9px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f8fafc;
    color: #334155;
    font-size: 13px;
    line-height: 1.2;
    text-decoration: none;
  }

  .datasheet-link:hover {
    border-color: #1684ff;
    color: #1684ff;
  }

  .datasheet-link img {
    width: 13px;
    height: 13px;
  }

  .param-cell span {
    color: #8a8f99;
  }

  .stock-cell b,
  .quantity-cell b,
  .price-cell .first,
  .price-cell .first b {
    color: #ff4d00;
  }

  .price-cell b {
    margin-left: 8px;
    color: #344054;
    font-weight: 400;
  }

  .stepper {
    width: 100%;
    max-width: 126px;
    height: 32px;
    display: grid;
    grid-template-columns: 32px 1fr 32px;
    border: 1px solid #d5d9e2;
    border-radius: 6px;
    overflow: hidden;
  }

  .stepper button,
  .stepper input {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    border: 0;
    border-right: 1px solid #d5d9e2;
    background: #fff;
    text-align: center;
    outline: 0;
  }

  .stepper button:last-child {
    border-right: 0;
  }

  .stepper button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .stepper button img {
    width: 13px;
    height: 13px;
  }

  .quantity-cell p {
    margin-top: 8px;
  }

  .quantity-cell span {
    display: inline-block;
    margin-top: 22px;
    padding: 4px 8px;
    border-radius: 12px;
    background: #f6f7fb;
    color: #667085;
    font-size: 12px;
  }

  .quantity-cell strong {
    color: #ff4d00;
  }

  .action-cell {
    display: grid;
    align-content: start;
    gap: 10px;
  }

  .action-cell button {
    width: 100%;
    max-width: 118px;
    height: 32px;
    border: 1px solid #1684ff;
    border-radius: 4px;
    background: #fff;
    color: #1684ff;
    font-weight: 700;
    cursor: pointer;
  }

  .action-cell .buy-btn {
    background: #1684ff;
    color: #fff;
  }

  .empty {
    padding: 100px 0;
    border-radius: 0 0 8px 8px;
    background: #fff;
    text-align: center;
    color: #64748b;
  }
  .search-empty-ad { min-height: 420px; padding: 54px 72px; display: flex; justify-content: center; gap: 34px; border-radius: 0 0 8px 8px; background: #fff; box-sizing: border-box; }
  .search-globe { width: 94px; height: 94px; flex: 0 0 94px; border: 4px dashed #70d8ef; border-radius: 50%; color: #374151; font-size: 82px; line-height: 82px; text-align: center; }
  .search-empty-content { min-width: 520px; }
  .search-empty-content h2 { margin: 0 0 16px; color: #4b5563; font-size: 22px; font-weight: 700; }
  .search-empty-content h2 strong { color: #ff7a00; }
  .search-empty-content h2 span { margin-left: 10px; padding: 5px 10px; border-radius: 16px; background: #edf6ff; color: #3b82f6; font-size: 13px; white-space: nowrap; }
  .search-empty-content > p { margin-bottom: 22px; color: #687385; font-size: 15px; line-height: 1.8; }
  .search-empty-actions { display: flex; gap: 12px; }
  .search-empty-actions button, .search-empty-actions a { width: 224px; height: 48px; display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 5px; color: #fff; font-size: 17px; cursor: pointer; box-sizing: border-box; }
  .search-empty-actions .manager-button { background: #ff9800; }
  .search-empty-actions a { background: #1296db; }
  .search-contact-qr { width: 158px; margin-top: 16px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 6px; background: #fff; text-align: center; box-shadow: 0 4px 14px rgba(15, 23, 42, .08); }
  .search-contact-qr img { width: 134px; height: 134px; display: block; object-fit: contain; }
  .search-contact-qr span { color: #4b5563; font-size: 15px; letter-spacing: 3px; }
</style>
